/**
 * Publish Flow decision artifacts service (Phase 6.y: data layer).
 *
 * Reads the Phase 6.x backend endpoints:
 *   GET /publish-flow/{jazelId}/{correlationId}/stage-decisions/{stage}?pageSize=&cursor=
 *   GET /publish-flow/{jazelId}/{correlationId}/vin/{vin}/trail
 *
 * The backend allow-list expanded as decision instrumentation went in:
 * Phase 6 shipped `'listingRules'`, Phase 7 added `'groups'`, Phase 8
 * added `'fieldMapping'`, Phase 10.3 added `'feedFieldRules'`, and Phase
 * 10.4 added `'preProcessor'`. The typed
 * `PublishFlowSupportedDecisionStage` mirrors that allow-list so call
 * sites can't accidentally request a stage the writer has not produced
 * for actual publishes.
 *
 * `PublishFlowVinTrailStageEntry` also carries optional `truncated` /
 * `truncationReason` fields. They are universal on the entry so any
 * stage can adopt the bounded-read pattern without reshaping the
 * contract.
 *
 * Design parity with `publishFlowObservabilityService.ts` and
 * `publishPreviewArtifactsService.ts`:
 *   - Two API surfaces per endpoint:
 *       1. Strict (`getPublishFlow*`) — throws on any non-200.
 *       2. Fallback (`tryGetPublishFlow*`) — returns a discriminated
 *          union (`StageDecisionsReadResult` for stage-decisions,
 *          `ArtifactReadResult<PublishVinTrail>` for vin-trail).
 *   - Authorization via `Authorization: Bearer <sessionToken>`.
 *   - Same fallback semantics as the sibling services: `notFound` for
 *     known absent codes, `unavailable` for network / 5xx / route 404s.
 *
 * Errors that ALWAYS THROW (both strict and `try*`):
 *   - 401 / 403                         — auth / authz failures
 *   - 400 MISSING_PATH_PARAM            — programmer error
 *   - 400 INVALID_STAGE                 — programmer error
 *   - 400 INVALID_PAGE_SIZE             — programmer error
 *   - 400 INVALID_CURSOR                — programmer error
 *
 * The cursor token is OPAQUE on the client. We never decode or inspect it;
 * we only round-trip it through the `cursor` query parameter.
 *
 * The Phase 6.x handler in
 * `ise/ise-srp-back/amplify/functions/publish_flow_observability_api/handler.ts`
 * is the source of truth for response codes; this service mirrors them
 * locally because `@jazel-auto/ise-contracts` does not yet publish the
 * Publish Flow decision shapes.
 */

import {
  type ArtifactReadResult,
  type PublishDecisionRecord,
  type PublishObservedStage,
  type PublishStageAvailability,
  type PublishStageDecisionsPage as PreviewStageDecisionsPage,
} from '@/services/publishPreviewArtifactsService'

// ---------------------------------------------------------------------------
// Phase 6.y-specific types.
//
// The Preview's `PublishVinTrailStageEntry` describes one-record-per-VIN
// (its trail aggregator collapses multiple matching records to the
// freshest). The Phase 6.x backend reader returns ALL matching records
// per stage so the UI can show every rule hit. We model that here
// rather than mutating the Preview contract — keeping the two surfaces
// independent honors the long-term separation between Preview and
// Publish Flow artifacts.
//
// If/when `@jazel-auto/ise-contracts` publishes the Phase 6.x shapes,
// these locals can be replaced wholesale.
// ---------------------------------------------------------------------------

export type PublishFlowVinTrailStageEntry = {
  availability: PublishStageAvailability
  /** Present iff availability === 'available'; may be empty if no record matched. */
  records?: PublishDecisionRecord[]
  /** unsupportedReason / deferredReason from the manifest, when not available. */
  reason?: string
  /**
   * True when the backend's bounded VIN-trail reader hit a hard cap
   * (records / bytes / parts) and stopped walking further artifact
   * parts for this stage. The fields below are universal on the entry
   * so any stage can adopt the same bounded-read pattern without
   * re-shaping the contract.
   *
   * The `truncationReason` token here is consumed by telemetry only and
   * MUST NOT reach the DOM (banned-substring contract enforced by unit
   * tests).
   */
  truncated?: true
  truncationReason?: 'maxRecords' | 'maxBytes' | 'maxParts'
}

export type PublishFlowVinTrail = {
  vin: string
  stages: Partial<Record<PublishObservedStage, PublishFlowVinTrailStageEntry>>
  parseWarnings?: unknown
  truncated?: { cause: 'maxBytes' | 'maxArtifacts'; stage?: PublishObservedStage }
}

// ---------------------------------------------------------------------------
// Re-exports for downstream consumers (UI components, tests).
//
// The Preview-shaped `PublishVinTrail` / `PublishVinTrailStageEntry`
// names are intentionally NOT re-exported here so call sites have to
// pick the publish-flow-specific shape explicitly.
// ---------------------------------------------------------------------------

export type {
  ArtifactReadResult,
  PublishDecisionRecord,
}

/**
 * Backwards-compatibility alias for the Phase 6.x VIN trail shape.
 * Equivalent to `PublishFlowVinTrail`; kept so existing call sites
 * (store, dialog) can keep using the shorter name.
 */
export type PublishVinTrail = PublishFlowVinTrail

/**
 * Stages currently supported by the actual-publish decision writer + read
 * API in `ise-srp-back`. Extending this set requires a corresponding
 * writer + handler change.
 *
 * Phase 6 shipped `'listingRules'`; Phase 7 added `'groups'`; Phase 8
 * added `'fieldMapping'`; Phase 10.3 added `'feedFieldRules'`; Phase
 * 10.4 added `'preProcessor'`. Note that Preview's
 * `PublishSupportedDecisionStage` is a different surface
 * (`'fieldMapping' | 'listingRules'`) and is intentionally NOT a
 * superset — Preview and Publish Flow each own their own allow-list.
 *
 * `Extract<>` over `PublishObservedStage` keeps the type tied to the
 * canonical stage enum so misspellings fail at compile time.
 */
export type PublishFlowSupportedDecisionStage = Extract<
  PublishObservedStage,
  | 'listingRules'
  | 'groups'
  | 'fieldMapping'
  | 'preProcessor'
  | 'feedFieldRules'
>

/**
 * Run-wide current-group distribution returned on the `groups` stage-decisions
 * page. Computed server-side over the FULL artifact (never just the current
 * page), so the dialog header can describe the whole run. `groups[].count` is
 * the number of VINs currently assigned to that group; `changedCount` is the
 * number of vehicles whose group membership changed this run.
 */
export type PublishGroupsDistributionEntry = { group: string; count: number }
export type PublishGroupsDistribution = {
  groups: PublishGroupsDistributionEntry[]
  changedCount: number
  totalRecords: number
}

/**
 * Run-wide Website Visibility (inventory membership) summary returned on the
 * `listingRules` stage-decisions page. Computed server-side over the FULL
 * artifact (never just the current page), so the dialog header can describe the
 * whole run. `blocked` is the count surfaced as "N matched blocking rules".
 */
export type PublishVisibilitySummary = {
  evaluated: number
  accepted: number
  blocked: number
}

export type PublishStageDecisionsPage = Omit<PreviewStageDecisionsPage, 'stage'> & {
  stage: PublishFlowSupportedDecisionStage
  /**
   * Total records for the CURRENT filter mode (browse, search, changed-only, or
   * matched-blocking-only browse), as reported by the server. The heading
   * prefers this over the loaded-row count so paged sets read their full total.
   */
  totalRecords?: number
  /** Echoed search term (present only when the request carried `?search=`). */
  search?: string
  /** Echoed changed-only flag (present only on `groups` changed-only browse). */
  changedOnly?: boolean
  /**
   * Echoed matched-blocking-only flag (present only on `listingRules`
   * matched-blocking-only browse).
   */
  matchedBlockingOnly?: boolean
  /** Present only on the `groups` stage. */
  distribution?: PublishGroupsDistribution
  /** Present only on the `listingRules` stage. */
  visibility?: PublishVisibilitySummary
}

// ---------------------------------------------------------------------------
// Inventory Sync drill-down (Phase 3).
//
// The reconciled Inventory Sync artifact carries a purpose-built record shape
// (action + membership + reason + provenance), distinct from the shared
// `PublishDecisionRecord`. The read API serves it from the SAME
// `stage-decisions/inventorySync` endpoint with an optional `action` bucket
// filter. `'anomaly'` is a pseudo-bucket selecting records carrying an anomaly
// marker regardless of action.
// ---------------------------------------------------------------------------

export type PublishFlowInventorySyncAction =
  | 'continued'
  | 'held_back'
  | 'admitted_outside_feed_prep'
  | 'removed'
  | 'reconciled_out'

/** Drill-down bucket selector: the five actions plus the `anomaly` pseudo-bucket. */
export type PublishFlowInventorySyncBucket = PublishFlowInventorySyncAction | 'anomaly'

export type PublishFlowInventorySyncGroup =
  | 'feedDerivedFunnel'
  | 'priorInventoryReconciliation'

/**
 * Matched Publish Rule evidence for a held_back VIN. Mirrors the backend
 * `InventorySyncRuleEvidence`: every field is optional and present only when
 * the snapshot guardrail surfaced it for this specific VIN.
 */
export type PublishFlowInventorySyncRuleEvidence = {
  ruleId?: string
  ruleName?: string
  ruleReason?: string
  configType?: string
  field?: string
  operator?: string
  expectedValue?: unknown
  actualValue?: unknown
}

export type PublishFlowInventorySyncProvenance = {
  feedSources?: string[]
  archivedAt?: string
  removalReason?: string
  /** Matched Publish Rule evidence for a held_back VIN, when captured. */
  rule?: PublishFlowInventorySyncRuleEvidence
  /**
   * Whether matched-rule evidence was captured for this held_back VIN. `false`
   * signals the held-back cause is known but the specific rule was not captured
   * (so the UI shows an honest "rule details not captured" state).
   */
  ruleProvenanceAvailable?: boolean
}

export type PublishFlowInventorySyncAnomaly = {
  code: 'group_membership_mismatch' | 'reconciliation_conflict'
  expectedGroup?: PublishFlowInventorySyncGroup
  observedGroup?: PublishFlowInventorySyncGroup
  detail?: string
}

export type PublishFlowInventorySyncDecisionRecord = {
  vin: string
  stage: 'inventorySync'
  action: PublishFlowInventorySyncAction
  status: 'changed' | 'unchanged' | 'skipped'
  inventorySyncGroup: PublishFlowInventorySyncGroup
  inFeedPreparationPassThrough: boolean
  reason: { code: string; explanation: string }
  provenance?: PublishFlowInventorySyncProvenance
  anomaly?: PublishFlowInventorySyncAnomaly
  generatedAt?: string
}

export type PublishFlowInventorySyncDecisionsPage = {
  stage: 'inventorySync'
  pageSize: number
  totalRecords: number
  records: PublishFlowInventorySyncDecisionRecord[]
  /** Echoed bucket filter when one was applied. */
  action?: PublishFlowInventorySyncBucket
  /** Opaque; round-trip untouched. Absent when there are no more records. */
  nextCursor?: string
}

export type InventorySyncDecisionsReadResult =
  | { status: 'ok'; data: PublishFlowInventorySyncDecisionsPage }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

// ---------------------------------------------------------------------------
// Internal helpers (mirror publishFlowObservabilityService.ts patterns).
// ---------------------------------------------------------------------------

const trimBase = (endpoint: string) => endpoint.replace(/\/+$/g, '')

const authHeaders = (sessionToken: string) => ({
  Accept: 'application/json',
  Authorization: `Bearer ${sessionToken}`,
})

type RawErrorPayload = {
  error?: string
  message?: string
  code?: string
}

type RawArtifactResponse =
  | { ok: true; data: unknown }
  | { ok: false; status: number; payload: RawErrorPayload | null }

async function requestArtifact(input: {
  endpoint: string
  path: string
  query?: URLSearchParams
  sessionToken: string
}): Promise<RawArtifactResponse> {
  const base = trimBase(input.endpoint)
  const qs = input.query?.toString()
  const url = `${base}${input.path}${qs && qs.length > 0 ? `?${qs}` : ''}`
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(input.sessionToken),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      payload: (payload ?? null) as RawErrorPayload | null,
    }
  }
  // Phase 6.x always returns `{ success: true, data: ... }` for 200s.
  // Defensive fallback to the raw body in case a future deployment skips
  // the envelope (matches the sibling services' behavior).
  const data =
    payload && typeof payload === 'object' && 'data' in payload
      ? (payload as { data: unknown }).data
      : payload
  return { ok: true, data }
}

function strictErrorMessage(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
  fallbackPrefix: string,
): string {
  return (
    res.payload?.message ||
    res.payload?.error ||
    `${fallbackPrefix} (${res.status})`
  )
}

/**
 * 4xx codes that ALWAYS throw — auth failures and programmer errors.
 * The full set is enumerated here so reviewers can see the contract at a
 * glance rather than chasing it through call sites.
 */
const ALWAYS_THROW_CODES = new Set<string>([
  'MISSING_PATH_PARAM',
  'INVALID_STAGE',
  'INVALID_PAGE_SIZE',
  'INVALID_CURSOR',
])

function shouldAlwaysThrow(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
): boolean {
  if (res.status === 401 || res.status === 403) return true
  const code = res.payload?.code
  return Boolean(code && ALWAYS_THROW_CODES.has(code))
}

function unavailableReason(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
  options: { recognizedNotFoundCodes?: Set<string> } = {},
): { reason: 'http5xx' | 'route'; message: string } | null {
  if (res.status >= 500) {
    return {
      reason: 'http5xx',
      message: strictErrorMessage(res, 'Publish flow details unavailable'),
    }
  }
  // A 404 falls into one of three buckets per endpoint:
  //   1. Code is in the endpoint's recognized notFound set
  //      → handled upstream as `notFound`; we return null here.
  //   2. Code is missing OR present but UNrecognized for THIS endpoint
  //      → treat as "the route exists but doesn't recognize this case",
  //        which most commonly means "older backend without the new
  //        endpoint". Surface as `unavailable: route` rather than
  //        bubbling a programmer-error throw.
  //   3. Anything else → null (caller throws strictly).
  if (res.status === 404) {
    const code = res.payload?.code
    const recognized = options.recognizedNotFoundCodes
    if (!code || (recognized && !recognized.has(code))) {
      return {
        reason: 'route',
        message: strictErrorMessage(res, 'Publish flow details route missing'),
      }
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Stage decisions.
// ---------------------------------------------------------------------------

/**
 * 404 codes the backend uses when the requested artifact is genuinely
 * absent (manifest missing OR pointer absent for a stage). We map ALL of
 * them to `notFound` so the UI can render a single "no decision artifacts
 * for this run" surface — distinguishing them is an on-call concern, not
 * a dealer one.
 */
const STAGE_DECISIONS_NOT_FOUND_CODES = new Set([
  'PUBLISH_OBSERVABILITY_MANIFEST_NOT_FOUND',
  'PUBLISH_DECISION_ARTIFACT_NOT_FOUND',
])

export type StageDecisionsReadResult =
  | { status: 'ok'; data: PublishStageDecisionsPage }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

function stageDecisionsPath(
  jazelId: string,
  correlationId: string,
  stage: PublishFlowSupportedDecisionStage,
): string {
  return `/publish-flow/${encodeURIComponent(jazelId)}/${encodeURIComponent(
    correlationId,
  )}/stage-decisions/${encodeURIComponent(stage)}`
}

function stageDecisionsQuery(input: {
  pageSize?: number
  cursor?: string
  search?: string
  changedOnly?: boolean
  matchedBlockingOnly?: boolean
  action?: string
}): URLSearchParams | undefined {
  const query = new URLSearchParams()
  if (typeof input.pageSize === 'number' && Number.isFinite(input.pageSize)) {
    query.set('pageSize', String(input.pageSize))
  }
  // Cursor is opaque; round-trip it untouched.
  if (typeof input.cursor === 'string' && input.cursor.length > 0) {
    query.set('cursor', input.cursor)
  }
  // Optional server-side search. `fieldMapping` and `groups` honor this today
  // (VIN OR field/group, case-insensitive substring); other stages ignore it.
  // We send it whenever a non-empty term is provided so the caller can locate
  // decisions that are not in the first browse page.
  if (typeof input.search === 'string' && input.search.trim().length > 0) {
    query.set('search', input.search.trim())
  }
  // Optional changed-only browse filter. Only `groups` honors this today. It is
  // mutually exclusive with `search` on the caller side: the dialog sends
  // `changedOnly=true` for default browse and `search=<term>` (without
  // `changedOnly`) for search.
  if (input.changedOnly === true) {
    query.set('changedOnly', 'true')
  }
  // Optional matched-blocking-only browse filter. Only `listingRules` honors
  // this today. It is mutually exclusive with `search` on the caller side: the
  // dialog sends `matchedBlockingOnly=true` for default Website Visibility
  // browse and `search=<term>` (without `matchedBlockingOnly`) for search.
  if (input.matchedBlockingOnly === true) {
    query.set('matchedBlockingOnly', 'true')
  }
  // Optional Inventory Sync drill-down bucket filter. Only `inventorySync`
  // honors this today. When set, `totalRecords` / `nextCursor` describe the
  // FILTERED bucket, so callers must thread the same `action` while paging.
  if (typeof input.action === 'string' && input.action.length > 0) {
    query.set('action', input.action)
  }
  return query.toString().length > 0 ? query : undefined
}

export async function getPublishFlowStageDecisions(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  stage: PublishFlowSupportedDecisionStage
  sessionToken: string
  pageSize?: number
  cursor?: string
}): Promise<PublishStageDecisionsPage> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: stageDecisionsPath(params.jazelId, params.correlationId, params.stage),
    query: stageDecisionsQuery({ pageSize: params.pageSize, cursor: params.cursor }),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(
      strictErrorMessage(
        res,
        `Failed to fetch publish flow decisions for ${params.stage}`,
      ),
    )
  }
  return res.data as PublishStageDecisionsPage
}

export async function tryGetPublishFlowStageDecisions(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  stage: PublishFlowSupportedDecisionStage
  sessionToken: string
  pageSize?: number
  cursor?: string
  /**
   * Optional server-side search term. Only `fieldMapping` honors it today
   * (VIN OR reasons[].field). When set, `totalRecords` / `nextCursor` describe
   * the MATCHED set, so callers must thread the same term while paging.
   */
  search?: string
  /**
   * Optional changed-only browse filter. Only `groups` honors it today. When
   * `true`, `totalRecords` / `nextCursor` describe the CHANGED set, so callers
   * must thread the same flag while paging. Mutually exclusive with `search`:
   * the dialog never sends both.
   */
  changedOnly?: boolean
  /**
   * Optional matched-blocking-only browse filter. Only `listingRules` honors it
   * today. When `true`, `totalRecords` / `nextCursor` describe the BLOCKED set,
   * so callers must thread the same flag while paging. Mutually exclusive with
   * `search`: the dialog never sends both.
   */
  matchedBlockingOnly?: boolean
}): Promise<StageDecisionsReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: stageDecisionsPath(params.jazelId, params.correlationId, params.stage),
      query: stageDecisionsQuery({
        pageSize: params.pageSize,
        cursor: params.cursor,
        search: params.search,
        changedOnly: params.changedOnly,
        matchedBlockingOnly: params.matchedBlockingOnly,
      }),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishStageDecisionsPage }
  if (shouldAlwaysThrow(res)) {
    throw new Error(
      strictErrorMessage(
        res,
        `Failed to fetch publish flow decisions for ${params.stage}`,
      ),
    )
  }
  if (
    res.status === 404
    && res.payload?.code
    && STAGE_DECISIONS_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(
        res,
        `Decisions for ${params.stage} not found`,
      ),
    }
  }
  const unavailable = unavailableReason(res, {
    recognizedNotFoundCodes: STAGE_DECISIONS_NOT_FOUND_CODES,
  })
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }
  throw new Error(
    strictErrorMessage(
      res,
      `Failed to fetch publish flow decisions for ${params.stage}`,
    ),
  )
}

/**
 * Paged browse of reconciled Inventory Sync decision records, optionally
 * filtered to a single funnel bucket (`action`). Mirrors the `try*` fallback
 * semantics of the sibling readers. When `action` is provided, `totalRecords`
 * and `nextCursor` describe the FILTERED bucket, so callers must thread the
 * same `action` while paging.
 */
export async function tryGetPublishFlowInventorySyncDecisions(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
  pageSize?: number
  cursor?: string
  action?: PublishFlowInventorySyncBucket
}): Promise<InventorySyncDecisionsReadResult> {
  // `inventorySync` is served by the same stage-decisions endpoint but is not
  // part of the detailed-impact "browser" decision-stage union (it has its own
  // self-contained panel), so the path is built directly here.
  const path = `/publish-flow/${encodeURIComponent(params.jazelId)}/${encodeURIComponent(
    params.correlationId,
  )}/stage-decisions/inventorySync`
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path,
      query: stageDecisionsQuery({
        pageSize: params.pageSize,
        cursor: params.cursor,
        action: params.action,
      }),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) {
    return { status: 'ok', data: res.data as PublishFlowInventorySyncDecisionsPage }
  }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch inventory sync decisions'))
  }
  if (
    res.status === 404
    && res.payload?.code
    && STAGE_DECISIONS_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Inventory sync decisions not found'),
    }
  }
  const unavailable = unavailableReason(res, {
    recognizedNotFoundCodes: STAGE_DECISIONS_NOT_FOUND_CODES,
  })
  if (unavailable) return { status: 'unavailable', ...unavailable }
  throw new Error(strictErrorMessage(res, 'Failed to fetch inventory sync decisions'))
}

// ---------------------------------------------------------------------------
// Feed Preparation pass-through (direct-account / no-preProcessor runs).
//
// Served by the SAME `stage-decisions/preProcessor` endpoint with
// `?mode=passThrough`. This is NOT a new visible stage — the records describe
// VINs that passed Feed Preparation unchanged because Feed Preparation was
// not needed. Two capabilities:
//   - paged browse (`getPublishFlowPassThroughDecisions`)
//   - server-side VIN search (`searchPublishFlowPassThroughVin`) which queries
//     the canonical vin-index across ALL parts, not just loaded rows.
// ---------------------------------------------------------------------------

export type PublishFlowPassThroughPage = {
  stage: 'preProcessor'
  mode: 'passThrough'
  records: PublishDecisionRecord[]
  pageSize: number
  totalRecords: number
  partIndex: number
  totalParts: number
  /** Opaque; round-trip untouched. Absent when there are no more records. */
  nextCursor?: string
}

export type PublishFlowPassThroughSearchResult = {
  stage: 'preProcessor'
  mode: 'passThrough'
  records: PublishDecisionRecord[]
  pageSize: number
  totalRecords: number
  search: {
    vin: string
    matched: boolean
    reason?: 'vin_not_found' | 'vin_index_missing'
  }
}

export type PassThroughBrowseReadResult =
  | { status: 'ok'; data: PublishFlowPassThroughPage }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type PassThroughSearchReadResult =
  | { status: 'ok'; data: PublishFlowPassThroughSearchResult }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

function passThroughQuery(input: {
  pageSize?: number
  cursor?: string
  vin?: string
}): URLSearchParams {
  const query = new URLSearchParams()
  query.set('mode', 'passThrough')
  if (typeof input.vin === 'string' && input.vin.length > 0) {
    query.set('vin', input.vin)
  } else {
    if (typeof input.pageSize === 'number' && Number.isFinite(input.pageSize)) {
      query.set('pageSize', String(input.pageSize))
    }
    if (typeof input.cursor === 'string' && input.cursor.length > 0) {
      query.set('cursor', input.cursor)
    }
  }
  return query
}

/** Paged browse of the pass-through VIN list. */
export async function getPublishFlowPassThroughDecisions(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
  pageSize?: number
  cursor?: string
}): Promise<PassThroughBrowseReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: stageDecisionsPath(params.jazelId, params.correlationId, 'preProcessor'),
      query: passThroughQuery({ pageSize: params.pageSize, cursor: params.cursor }),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishFlowPassThroughPage }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch Feed Preparation pass-through VINs'))
  }
  if (
    res.status === 404
    && res.payload?.code
    && STAGE_DECISIONS_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Feed Preparation pass-through VINs not found'),
    }
  }
  const unavailable = unavailableReason(res, {
    recognizedNotFoundCodes: STAGE_DECISIONS_NOT_FOUND_CODES,
  })
  if (unavailable) return { status: 'unavailable', ...unavailable }
  throw new Error(strictErrorMessage(res, 'Failed to fetch Feed Preparation pass-through VINs'))
}

/** Server-side VIN search across ALL pass-through parts via the vin-index. */
export async function searchPublishFlowPassThroughVin(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
  /** Normalized (trim + uppercase) by the caller; backend also normalizes. */
  vin: string
}): Promise<PassThroughSearchReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: stageDecisionsPath(params.jazelId, params.correlationId, 'preProcessor'),
      query: passThroughQuery({ vin: params.vin }),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishFlowPassThroughSearchResult }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to search Feed Preparation pass-through VINs'))
  }
  if (
    res.status === 404
    && res.payload?.code
    && STAGE_DECISIONS_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Feed Preparation pass-through VINs not found'),
    }
  }
  const unavailable = unavailableReason(res, {
    recognizedNotFoundCodes: STAGE_DECISIONS_NOT_FOUND_CODES,
  })
  if (unavailable) return { status: 'unavailable', ...unavailable }
  throw new Error(strictErrorMessage(res, 'Failed to search Feed Preparation pass-through VINs'))
}

// ---------------------------------------------------------------------------
// VIN trail.
// ---------------------------------------------------------------------------

/**
 * VIN-trail returns 200 even for unknown VINs (it just echoes the
 * requested vin and reports `availability` per stage). The only 404 path
 * is manifest-missing.
 */
const VIN_TRAIL_NOT_FOUND_CODES = new Set([
  'PUBLISH_OBSERVABILITY_MANIFEST_NOT_FOUND',
])

function vinTrailPath(jazelId: string, correlationId: string, vin: string): string {
  // VIN is `encodeURIComponent`-encoded but NOT uppercased here; the
  // backend handles case-insensitive matching after URL-decoding.
  return `/publish-flow/${encodeURIComponent(jazelId)}/${encodeURIComponent(
    correlationId,
  )}/vin/${encodeURIComponent(vin)}/trail`
}

export async function getPublishFlowVinTrail(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  vin: string
  sessionToken: string
}): Promise<PublishVinTrail> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: vinTrailPath(params.jazelId, params.correlationId, params.vin),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow VIN trail'))
  }
  return res.data as PublishVinTrail
}

export async function tryGetPublishFlowVinTrail(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  vin: string
  sessionToken: string
}): Promise<ArtifactReadResult<PublishVinTrail>> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: vinTrailPath(params.jazelId, params.correlationId, params.vin),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishVinTrail }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow VIN trail'))
  }
  if (
    res.status === 404
    && res.payload?.code
    && VIN_TRAIL_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Publish flow VIN trail not found'),
    }
  }
  const unavailable = unavailableReason(res, {
    recognizedNotFoundCodes: VIN_TRAIL_NOT_FOUND_CODES,
  })
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }
  throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow VIN trail'))
}
